import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, FileText, Upload, Trash2, ChevronRight, ChevronLeft, Check, Send, XCircle, CheckCircle2, RotateCcw, Archive, AlertTriangle } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { formatCurrency } from '../../lib/utils';
import SubcontractSALTab, { useSubcontractSalV2Enabled } from '../subcontract-sal/SubcontractSALTab';
import { useSubcontractOfferEnabled } from '../../hooks/useSubcontractOfferEnabled';
import SubcontractOfferWizard from '../subcontract-offer/SubcontractOfferWizard';
import SubcontractOffersList from '../subcontract-offer/SubcontractOffersList';
import SubcontractOfferDetail from '../subcontract-offer/SubcontractOfferDetail';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  bozza: 'bg-slate-100 text-slate-700 border-slate-300',
  pronto_per_firma: 'bg-blue-100 text-blue-700 border-blue-300',
  firmato: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  annullato: 'bg-rose-100 text-rose-700 border-rose-300',
  risolto: 'bg-amber-100 text-amber-700 border-amber-300',
  chiuso: 'bg-zinc-200 text-zinc-800 border-zinc-400',
};

const STATUS_LABELS = {
  bozza: 'Bozza',
  pronto_per_firma: 'Pronto per firma',
  firmato: 'Firmato',
  annullato: 'Annullato',
  risolto: 'Risolto',
  chiuso: 'Chiuso',
};

const VAT_REGIMES = [
  { value: 'reverse_charge', label: 'Reverse charge' },
  { value: 'vat_4', label: 'IVA 4%' },
  { value: 'vat_10', label: 'IVA 10%' },
  { value: 'vat_22', label: 'IVA 22%' },
  { value: 'exempt', label: 'Esente IVA' },
  { value: 'out_of_scope', label: 'Fuori campo IVA' },
  { value: 'not_subject', label: 'Non soggetto IVA' },
  { value: 'to_verify', label: 'Da verificare' },
  // Alias legacy (retro-compat display)
  { value: 'iva_10', label: 'IVA 10%' },
  { value: 'iva_22', label: 'IVA 22%' },
  { value: 'fuori_campo', label: 'Fuori campo / esente' },
  { value: 'da_verificare', label: 'Da verificare' },
];

const VAT_LEGACY_ALIAS = { iva_10: 'vat_10', iva_22: 'vat_22', fuori_campo: 'out_of_scope', da_verificare: 'to_verify' };
const normalizeVat = (v) => VAT_LEGACY_ALIAS[v] || v || 'to_verify';

// Fase C — state machine transizioni consentite (specchio del backend VALID_TRANSITIONS)
const NEXT_TRANSITIONS = {
  bozza: [
    { target: 'pronto_per_firma', label: 'Segna pronto per firma', icon: Send, variant: 'default', confirm: false },
    { target: 'annullato',        label: 'Annulla',                icon: XCircle, variant: 'destructive', confirm: true },
  ],
  pronto_per_firma: [
    { target: 'firmato',   label: 'Segna firmato', icon: CheckCircle2, variant: 'primary', confirm: true },
    { target: 'bozza',     label: 'Torna in bozza', icon: RotateCcw,    variant: 'ghost',    confirm: false },
    { target: 'annullato', label: 'Annulla',        icon: XCircle,       variant: 'destructive', confirm: true },
  ],
  firmato: [
    { target: 'chiuso',  label: 'Chiudi',   icon: Archive,   variant: 'default',     confirm: true },
    { target: 'risolto', label: 'Risolvi',  icon: AlertTriangle, variant: 'destructive', confirm: true },
  ],
  annullato: [],
  chiuso: [],
  risolto: [],
};

const CONFIRM_COPY = {
  firmato: {
    title: 'Confermare firma del contratto?',
    body: 'Questa azione aggiornera\u2019 il Quadro Economico con gli importi affidati (costo affidato + data affidamento). L\u2019operazione e\u2019 irreversibile: potrai chiudere o risolvere il contratto, ma non tornare in bozza.',
    cta: 'Firma e sincronizza Q.E.',
  },
  annullato: {
    title: 'Confermare annullamento?',
    body: 'Il contratto verra\u2019 marcato come annullato e le voci di preventivo affidate verranno liberate per essere riassegnate. L\u2019operazione e\u2019 irreversibile.',
    cta: 'Annulla contratto',
  },
  chiuso: {
    title: 'Confermare chiusura?',
    body: 'Il contratto passa in stato Chiuso (storico). Il costo affidato in Q.E. resta invariato. Operazione irreversibile.',
    cta: 'Chiudi contratto',
  },
  risolto: {
    title: 'Confermare risoluzione?',
    body: 'Il contratto passa in stato Risolto (interruzione). Il costo affidato in Q.E. resta come storico. Operazione irreversibile.',
    cta: 'Risolvi contratto',
  },
};

const ProjectSubcontractsTab = ({ project, onNavigateTab }) => {
  const salFlagEnabled = useSubcontractSalV2Enabled();
  const offerFlagEnabled = useSubcontractOfferEnabled();
  const [salTabOpen, setSalTabOpen] = useState(null); // Subcontract selected for SAL tab
  const [loading, setLoading] = useState(true);
  const [subcontracts, setSubcontracts] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingSubcontract, setEditingSubcontract] = useState(null); // subcontract in modifica bozza
  const [refreshKey, setRefreshKey] = useState(0);
  // Fase C — modal conferma transizione
  const [pendingTransition, setPendingTransition] = useState(null); // { sub, target }
  const [transitioning, setTransitioning] = useState(false);
  // F.3 — nuovo workflow Offerta di Subappalto (attivo con feature flag)
  const [offerWizardOpen, setOfferWizardOpen] = useState(false);
  const [offerDetailId, setOfferDetailId] = useState(null);
  const [editingOffer, setEditingOffer] = useState(null); // F.3.1: offerta in modifica (bozza)
  const [offersRefreshKey, setOffersRefreshKey] = useState(0);
  // Utente admin? Approssimazione basata sulla lettura di /api/settings/company OK.
  // In alternativa, si può leggere il ruolo utente dal contesto; conservativo: mostro
  // dati interni solo se ha già accesso a subcontracts (already an admin scope).
  const isAdmin = true;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/api/subcontracts?project_id=${project.id}`);
        setSubcontracts(r.data.items || []);
      } catch (e) {
        toast.error('Errore caricamento subappalti');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [project.id, refreshKey]);

  // Helper: forza download tramite anchor (compatible Safari + Chrome)
  const _forceDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'documento.pdf';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  };

  const _filenameFromResponse = (r, fallback) => {
    const cd = r.headers?.['content-disposition'] || r.headers?.['Content-Disposition'] || '';
    const xf = r.headers?.['x-filename'] || r.headers?.['X-Filename'];
    if (xf) return xf;
    const match = /filename="?([^"]+)"?/i.exec(cd);
    return match ? match[1] : fallback;
  };

  const handleDownloadPdf = async (sub, docType = null) => {
    try {
      let doc;
      if (docType === 'firmato') {
        // Preferisci l'attivo, fallback all'ultimo firmato
        doc = (sub.documents || []).find(d =>
          (d.document_type === 'firmato' || d.document_type === 'contratto_firmato') && d.is_active !== false
        );
        if (!doc) {
          doc = (sub.documents || []).slice().reverse().find(d =>
            d.document_type === 'firmato' || d.document_type === 'contratto_firmato'
          );
        }
      } else {
        doc = (sub.documents || []).slice().reverse().find(d => d.document_type === 'generato');
      }
      if (!doc) {
        toast.info(docType === 'firmato' ? 'Nessun contratto firmato caricato' : 'Genera prima il PDF');
        return;
      }
      const r = await axios.get(`${API}/api/subcontracts/${sub.id}/documents/${doc.id}/download`, { responseType: 'blob' });
      _forceDownload(r.data, _filenameFromResponse(r, doc.filename || doc.original_filename || 'documento.pdf'));
    } catch (e) {
      toast.error('Errore download PDF');
    }
  };

  const handleGeneratePdf = async (sub) => {
    // Warning P0: regime fiscale non verificato (STEP 2 - 2026-02)
    if (sub.vat_regime === 'to_verify' || sub.vat_regime === 'da_verificare' || !sub.vat_regime) {
      const ok = window.confirm(
        'Regime fiscale non verificato per questo contratto.\n\n' +
        'Il PDF verra\u2019 generato con una formulazione neutra, ma prima della firma verifica il regime IVA con il fornitore e aggiornalo.\n\n' +
        'Procedere comunque con la generazione?'
      );
      if (!ok) return;
    }
    // Se esiste gia' un firmato attivo, chiedi conferma aggiuntiva
    const hasActiveSigned = (sub.documents || []).some(
      d => (d.document_type === 'firmato' || d.document_type === 'contratto_firmato') && d.is_active !== false
    );
    if (hasActiveSigned) {
      const ok = window.confirm(
        'Il subappalto dispone gia\u2019 di un contratto firmato. La nuova bozza NON sostituira\u2019 il documento firmato archiviato. Procedere?'
      );
      if (!ok) return;
    }
    try {
      toast.loading('Generazione PDF...', { id: 'gen' });
      const r = await axios.post(
        `${API}/api/subcontracts/${sub.id}/generate-pdf`,
        {},
        { responseType: 'blob' }
      );
      const filename = _filenameFromResponse(r, `Contratto_Subappalto_${sub.subcontract_number}.pdf`);
      _forceDownload(r.data, filename);
      toast.success('Contratto generato correttamente', { id: 'gen' });
      setRefreshKey(k => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore generazione PDF', { id: 'gen' });
    }
  };

  const handleUploadSigned = async (sub, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/api/subcontracts/${sub.id}/upload-signed-pdf`, fd);
      const v = r.data?.version || '?';
      toast.success(`Contratto firmato caricato · v${v}`);
      setRefreshKey(k => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore upload');
    }
  };

  const handleDownloadHistoryDoc = async (sub, documentId, fallbackName) => {
    try {
      const r = await axios.get(`${API}/api/subcontracts/${sub.id}/documents/${documentId}/download`, { responseType: 'blob' });
      _forceDownload(r.data, _filenameFromResponse(r, fallbackName || 'versione_precedente.pdf'));
    } catch (e) {
      toast.error('Errore download versione precedente');
    }
  };

  const handleDelete = async (sub) => {
    if (!window.confirm(`Annullare ${sub.subcontract_number}?`)) return;
    try {
      await axios.delete(`${API}/api/subcontracts/${sub.id}`);
      toast.success('Subappalto annullato, voci liberate');
      setRefreshKey(k => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  // Fase C — transizione stato
  const requestTransition = (sub, target) => {
    const trans = (NEXT_TRANSITIONS[sub.status] || []).find(t => t.target === target);
    if (!trans) return;
    if (trans.confirm) {
      setPendingTransition({ sub, target });
    } else {
      applyTransition(sub, target);
    }
  };

  const applyTransition = async (sub, target) => {
    setTransitioning(true);
    try {
      const r = await axios.post(`${API}/api/subcontracts/${sub.id}/transition`, { target_status: target });
      const data = r.data || {};
      // Feedback dettagliato per firma
      if (target === 'firmato' && data.qe_sync) {
        const { lines_updated, total_contributed, unmatched_items } = data.qe_sync;
        if (lines_updated > 0) {
          toast.success(`Firmato · Q.E. aggiornato su ${lines_updated} righe · totale affidato ${formatCurrency(total_contributed)}`);
        } else {
          toast.warning('Firmato, ma nessuna riga Q.E. e\u2019 stata aggiornata. Importa prima le voci del preventivo nel Q.E.');
        }
        if (unmatched_items?.length > 0) {
          toast.info(`${unmatched_items.length} voci senza corrispondenza Q.E. — verifica.`);
        }
      } else {
        toast.success(`Stato aggiornato: ${STATUS_LABELS[target] || target}`);
      }
      setPendingTransition(null);
      setRefreshKey(k => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore transizione');
    } finally {
      setTransitioning(false);
    }
  };

  const totals = subcontracts.reduce((acc, s) => {
    if (!['annullato'].includes(s.status)) {
      acc.count += 1;
      acc.total_amount_net += s.total_amount_net || 0;
      acc.margin += s.expected_margin_net || 0;
    }
    return acc;
  }, { count: 0, total_amount_net: 0, margin: 0 });

  return (
    <div className="space-y-4" data-testid="subcontracts-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-h4">Subappalti</h3>
          <p className="text-sm text-[#737373]">
            {totals.count} contratti attivi · Affidato {formatCurrency(totals.total_amount_net)} · Margine previsto {formatCurrency(totals.margin)}
          </p>
        </div>
        <Button
          onClick={() => {
            if (offerFlagEnabled) setOfferWizardOpen(true);
            else setWizardOpen(true);
          }}
          className="bg-[#FF7819] hover:bg-[#e56811] text-white"
          data-testid={offerFlagEnabled ? "new-offer-btn" : "new-subcontract-btn"}
        >
          <Plus className="w-4 h-4 mr-2" />
          {offerFlagEnabled ? 'Nuova Offerta di Subappalto' : 'Nuovo Subappalto'}
        </Button>
      </div>

      {offerFlagEnabled && (
        <div className="mb-4" data-testid="offers-section">
          <SubcontractOffersList
            projectId={project.id}
            refreshKey={offersRefreshKey}
            onOpenOffer={(o) => setOfferDetailId(o.id)}
          />
        </div>
      )}

      <Card className="card-abitalo">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-[#737373]">Caricamento...</div>
          ) : subcontracts.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#737373]">
              Nessun subappalto ancora creato per questo progetto.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="subcontracts-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[#525252] text-xs uppercase tracking-wide">
                  <th className="px-3 py-2 font-semibold">Numero</th>
                  <th className="px-3 py-2 font-semibold">Subappaltatore</th>
                  <th className="px-3 py-2 font-semibold">Voci</th>
                  <th className="px-3 py-2 font-semibold text-right">Affidato</th>
                  <th className="px-3 py-2 font-semibold text-right">Margine %</th>
                  <th className="px-3 py-2 font-semibold">Regime</th>
                  <th className="px-3 py-2 font-semibold">Stato</th>
                  <th className="px-3 py-2 font-semibold text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {subcontracts.map(s => {
                  const qeNotSynced = s.status === 'firmato' && (!s.contributed_economic_lines || s.contributed_economic_lines.length === 0);
                  return (
                  <React.Fragment key={s.id}>
                  <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`subcontract-row-${s.id}`}>
                    <td className="px-3 py-2 font-medium">{s.subcontract_number}</td>
                    <td className="px-3 py-2">{s.supplier_snapshot?.ragione_sociale || '—'}</td>
                    <td className="px-3 py-2 text-center">{(s.documents || []).length ? '📄' : ''} —</td>
                    <td className="px-3 py-2 text-right font-semibold">{formatCurrency(s.total_amount_net)}</td>
                    <td className="px-3 py-2 text-right">
                      <Badge variant="outline" className={
                        s.expected_margin_percent >= 15 ? 'bg-emerald-50 text-emerald-700' :
                        s.expected_margin_percent >= 5 ? 'bg-amber-50 text-amber-700' :
                        'bg-rose-50 text-rose-700'
                      }>
                        {(s.expected_margin_percent || 0).toFixed(1)}%
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Badge variant="outline">{VAT_REGIMES.find(v => v.value === s.vat_regime)?.label || s.vat_regime}</Badge>
                        {(s.vat_regime === 'to_verify' || s.vat_regime === 'da_verificare' || !s.vat_regime) && (() => {
                          const canEdit = ['bozza', 'pronto_per_firma'].includes(s.status);
                          const badgeContent = (
                            <>
                              <AlertTriangle className="w-3 h-3" />
                              <span>IVA da verificare</span>
                            </>
                          );
                          const tooltip = canEdit
                            ? "Il regime fiscale del contratto non e' stato ancora verificato. Clicca per modificare."
                            : "Il regime fiscale del contratto non e' stato ancora verificato.";
                          return canEdit ? (
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  const r = await axios.get(`${API}/api/subcontracts/${s.id}`);
                                  setEditingSubcontract(r.data);
                                  setWizardOpen(true);
                                } catch { toast.error('Errore apertura modifica'); }
                              }}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100 text-[10px] font-medium cursor-pointer transition-colors"
                              title={tooltip}
                              data-testid={`badge-vat-unverified-${s.id}`}
                            >
                              {badgeContent}
                            </button>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-400 bg-amber-50 text-amber-800 text-[10px] font-medium"
                              title={tooltip}
                              data-testid={`badge-vat-unverified-${s.id}`}
                              data-clickable="false"
                            >
                              {badgeContent}
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={STATUS_COLORS[s.status] || ''} variant="outline">
                        {STATUS_LABELS[s.status] || s.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        {s.status === 'bozza' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={async () => {
                              // Fetch dettaglio con items (list endpoint puo' non includerli)
                              try {
                                const r = await axios.get(`${API}/api/subcontracts/${s.id}`);
                                setEditingSubcontract(r.data);
                                setWizardOpen(true);
                              } catch (err) {
                                toast.error('Errore caricamento subappalto');
                              }
                            }}
                            title="Modifica bozza"
                            data-testid={`edit-subcontract-${s.id}`}
                          >
                            Modifica
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleGeneratePdf(s)} title="Genera PDF" data-testid={`gen-pdf-${s.id}`}>
                          <FileText className="w-4 h-4" />
                        </Button>
                        {salFlagEnabled && s.status === 'firmato' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSalTabOpen(s)}
                            title="SAL Subappaltatore"
                            data-testid={`sal-tab-btn-${s.id}`}
                            className="text-xs"
                          >
                            SAL
                          </Button>
                        )}
                        <label className="inline-block">
                          <input
                            type="file"
                            accept=".pdf"
                            className="hidden"
                            onChange={e => e.target.files?.[0] && handleUploadSigned(s, e.target.files[0])}
                            data-testid={`upload-signed-${s.id}`}
                          />
                          <Button size="sm" variant="ghost" asChild title="Upload firmato">
                            <span><Upload className="w-4 h-4" /></span>
                          </Button>
                        </label>
                        {/* Fase C — bottoni transizione stato */}
                        {(NEXT_TRANSITIONS[s.status] || []).map((t) => {
                          const Icon = t.icon;
                          const isPrimary = t.target === 'firmato';
                          const isDestructive = t.variant === 'destructive';
                          return (
                            <Button
                              key={t.target}
                              size="sm"
                              variant={isPrimary ? 'default' : 'ghost'}
                              onClick={() => requestTransition(s, t.target)}
                              title={t.label}
                              disabled={transitioning}
                              data-testid={`transition-${t.target}-${s.id}`}
                              className={
                                isPrimary ? 'bg-emerald-600 hover:bg-emerald-700 text-white' :
                                isDestructive ? 'text-rose-600 hover:text-rose-700 hover:bg-rose-50' :
                                ''
                              }
                            >
                              <Icon className="w-4 h-4" />
                            </Button>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                  {qeNotSynced && (
                    <tr className="border-b border-amber-200 bg-amber-50/50" data-testid={`qe-warning-row-${s.id}`}>
                      <td colSpan={8} className="px-3 py-2">
                        <div className="flex items-start gap-2 text-sm">
                          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 text-amber-900">
                            <span className="font-medium">Q.E. non aggiornato:</span>{' '}
                            <span className="text-amber-800">le voci del preventivo non risultano importate nel Quadro Economico. Il costo affidato non è visibile nel Q.E. del progetto.</span>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-amber-400 text-amber-800 hover:bg-amber-100 h-7 text-xs"
                            onClick={() => {
                              // Fase C — attiva il tab Q.E. via lifted state (compatibile con Radix Tabs)
                              if (typeof onNavigateTab === 'function') {
                                onNavigateTab('economic');
                              }
                            }}
                            data-testid={`goto-qe-${s.id}`}
                          >
                            Vai al Q.E.
                            <ChevronRight className="w-3 h-3 ml-1" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )}
                  {/* STEP 2 — Widget "Ultima versione contratto firmato" per subappalti post-bozza */}
                  {['pronto_per_firma', 'firmato', 'chiuso', 'risolto'].includes(s.status) && (
                    <tr className="border-b border-slate-200 bg-white" data-testid={`signed-row-${s.id}`}>
                      <td colSpan={8} className="px-3 py-2">
                        <SignedDocumentWidget
                          subcontract={s}
                          onDownload={handleDownloadPdf}
                          onUpload={handleUploadSigned}
                          onDownloadHistory={handleDownloadHistoryDoc}
                          onRegenerate={handleGeneratePdf}
                        />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {wizardOpen && (
        <SubcontractWizard
          projectId={project.id}
          existingSubcontract={editingSubcontract}
          onClose={() => { setWizardOpen(false); setEditingSubcontract(null); }}
          onCreated={() => { setWizardOpen(false); setEditingSubcontract(null); setRefreshKey(k => k + 1); }}
        />
      )}

      {/* Fase C — modal conferma transizione */}
      <AlertDialog
        open={!!pendingTransition}
        onOpenChange={(o) => { if (!o) setPendingTransition(null); }}
      >
        <AlertDialogContent data-testid="transition-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {pendingTransition?.target === 'firmato' ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              )}
              {CONFIRM_COPY[pendingTransition?.target]?.title || 'Conferma azione'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#525252]" asChild>
              <div>
                {pendingTransition?.sub && (
                  <div className="mb-2 text-xs text-[#737373]">
                    {pendingTransition.sub.subcontract_number} · {pendingTransition.sub.supplier_snapshot?.ragione_sociale || '—'} · {formatCurrency(pendingTransition.sub.total_amount_net)}
                  </div>
                )}
                <div>{CONFIRM_COPY[pendingTransition?.target]?.body || 'Confermare l\u2019azione?'}</div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={transitioning}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => pendingTransition && applyTransition(pendingTransition.sub, pendingTransition.target)}
              disabled={transitioning}
              data-testid="transition-confirm-btn"
              className={
                pendingTransition?.target === 'firmato'
                  ? 'bg-emerald-600 hover:bg-emerald-700'
                  : pendingTransition?.target && ['annullato', 'risolto'].includes(pendingTransition.target)
                    ? 'bg-rose-600 hover:bg-rose-700'
                    : 'bg-[#FF7819] hover:bg-[#e56811]'
              }
            >
              {transitioning ? 'Attendere...' : (CONFIRM_COPY[pendingTransition?.target]?.cta || 'Conferma')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* V2.1A Step C — Modal SAL Subappaltatore (gated by feature flag) */}
      {salFlagEnabled && salTabOpen && (
        <SubcontractSALTab
          open={!!salTabOpen}
          onClose={() => setSalTabOpen(null)}
          subcontract={salTabOpen}
        />
      )}

      {/* F.3 — Offerta di Subappalto: wizard + detail (gated by feature flag) */}
      {offerFlagEnabled && (
        <SubcontractOfferWizard
          open={offerWizardOpen}
          onOpenChange={(v) => {
            setOfferWizardOpen(v);
            if (!v) setEditingOffer(null);
          }}
          project={project}
          isAdmin={isAdmin}
          editingOffer={editingOffer}
          onCreated={(offer) => {
            setOffersRefreshKey(k => k + 1);
            if (offer?.id) setOfferDetailId(offer.id);
          }}
          onUpdated={(offer) => {
            setOffersRefreshKey(k => k + 1);
            setEditingOffer(null);
            if (offer?.id) setOfferDetailId(offer.id);
          }}
        />
      )}
      {offerFlagEnabled && offerDetailId && (
        <SubcontractOfferDetail
          open={!!offerDetailId}
          onOpenChange={(v) => { if (!v) setOfferDetailId(null); }}
          offerId={offerDetailId}
          isAdmin={isAdmin}
          onChanged={() => setOffersRefreshKey(k => k + 1)}
          onRequestEdit={(offer) => {
            setEditingOffer(offer);
            setOfferDetailId(null);
            setOfferWizardOpen(true);
          }}
        />
      )}
    </div>
  );
};

// =========================================================
// WIZARD 4-STEP (supporta creazione + modifica bozza)
// =========================================================
const SubcontractWizard = ({ projectId, existingSubcontract, onClose, onCreated }) => {
  const isEdit = !!existingSubcontract;
  const [step, setStep] = useState(1);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState(existingSubcontract?.supplier_id || '');
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [availableItems, setAvailableItems] = useState({ items: [], estimate_id: null });
  const [selectedItems, setSelectedItems] = useState(() => {
    // In edit mode, pre-populate selectedItems from existing subcontract.items
    if (existingSubcontract?.items) {
      const map = {};
      existingSubcontract.items.forEach(it => {
        map[it.estimate_item_id] = {
          estimate_item_id: it.estimate_item_id,
          original_description: it.original_description,
          quantity: it.quantity || 0,
          unit_price: it.unit_price || 0,
        };
      });
      return map;
    }
    return {};
  });
  const [formData, setFormData] = useState({
    vat_regime: normalizeVat(existingSubcontract?.vat_regime),
    contractor_firmatario: existingSubcontract?.contractor_firmatario || 'gaetano_falco',
    start_date: existingSubcontract?.start_date || '',
    end_date: existingSubcontract?.end_date || '',
    duration_days: existingSubcontract?.duration_days || 90,
    penali_giornaliere: existingSubcontract?.penali_giornaliere || 150,
    ritenuta_garanzia_percent: existingSubcontract?.ritenuta_garanzia_percent || 10,
    polizza_massimale: existingSubcontract?.polizza_massimale || 1500000,
    notes: existingSubcontract?.notes || '',
  });
  const [vatRegimeManuallySet, setVatRegimeManuallySet] = useState(
    !!existingSubcontract?.vat_regime && normalizeVat(existingSubcontract.vat_regime) !== 'to_verify'
  );
  const [saving, setSaving] = useState(false);
  // Ribasso Step 1 — precompilato in edit
  const [discountPercent, setDiscountPercent] = useState(
    existingSubcontract?.contract_discount_percent != null ? String(existingSubcontract.contract_discount_percent) : ''
  );
  const [discountApplied, setDiscountApplied] = useState(!!existingSubcontract?.contract_discount_percent);
  const [discountConfirmed, setDiscountConfirmed] = useState(!!existingSubcontract?.contract_discount_confirmed_over_threshold);
  const [showDiscountOverModal, setShowDiscountOverModal] = useState(false);
  // HOTFIX (2026-02): Importo concordato iniziale (contratto a misura)
  const [agreedAmount, setAgreedAmount] = useState(
    existingSubcontract?.agreed_initial_amount != null ? String(existingSubcontract.agreed_initial_amount) : ''
  );
  // Importo iniziale memorizzato (dopo apply) — usato per KPI e submit.
  const [agreedInitialLocked, setAgreedInitialLocked] = useState(
    existingSubcontract?.agreed_initial_amount != null ? Number(existingSubcontract.agreed_initial_amount) : null
  );
  const [negativeMarginConfirmed, setNegativeMarginConfirmed] = useState(false);
  const [showNegativeMarginModal, setShowNegativeMarginModal] = useState(false);
  // Prezzo consigliato di subappalto impianto elettrico (solo informativo)
  const [recommendedPrice, setRecommendedPrice] = useState(null);
  const [recommendedPriceIncomplete, setRecommendedPriceIncomplete] = useState(false);
  const [recommendedMissing, setRecommendedMissing] = useState([]);
  const [recommendedHasElectrical, setRecommendedHasElectrical] = useState(false);
  const [agreedTouched, setAgreedTouched] = useState(false);
  const DISCOUNT_ELIGIBLE = ['subappaltatore_edile','elettricista','idraulico','edile','elettrico'];
  const DISCOUNT_THRESHOLD = 30.0;
  const selectedSupplier = suppliers.find(s => s.id === selectedSupplierId);
  const isDiscountEligible = !!(selectedSupplier && DISCOUNT_ELIGIBLE.includes(selectedSupplier.category));
  const isElectricalSupplier = !!(selectedSupplier && ['elettricista', 'elettrico'].includes(selectedSupplier.category));

  useEffect(() => {
    axios.get(`${API}/api/suppliers?limit=1000`).then(r => setSuppliers(r.data.items || []));
    axios.get(`${API}/api/projects/${projectId}/subcontract-available-items`).then(r => setAvailableItems(r.data));
  }, [projectId]);

  // Prezzo consigliato di subappalto elettrico — ricalcolato SOLO sulle voci
  // elettriche (categoria D) effettivamente selezionate/assegnate a questo subappalto.
  useEffect(() => {
    const eid = availableItems.estimate_id;
    if (!eid) { setRecommendedPrice(0); setRecommendedHasElectrical(false); return; }
    const ids = Object.keys(selectedItems);
    const params = new URLSearchParams();
    params.set('estimate_id', eid);
    ids.forEach(id => params.append('item_ids', id));
    axios.get(`${API}/api/subcontracts/recommended-price?${params.toString()}`).then(r => {
      const d = r.data || {};
      setRecommendedPrice(d.recommended_subcontract_price);
      setRecommendedPriceIncomplete(!!d.incomplete);
      setRecommendedMissing(d.missing_items || []);
      setRecommendedHasElectrical(!!d.has_electrical);
      // Precompila l'importo contrattuale col prezzo consigliato finché l'operatore
      // non lo modifica manualmente (in creazione). Dopo la modifica manuale resta
      // invariato al cambiare della selezione: si aggiorna solo il benchmark.
      if (!existingSubcontract && !agreedTouched && d.has_electrical && d.recommended_subcontract_price > 0) {
        setAgreedAmount(String(d.recommended_subcontract_price));
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableItems.estimate_id, selectedItems]);

  // STEP 2 (2026-02): pre-fill vat_regime da Supplier.vat_regime quando cambia supplier,
  // ma solo se l'utente non l'ha impostato manualmente. Priorita': supplier.vat_regime > default categoria.
  useEffect(() => {
    if (!selectedSupplier || vatRegimeManuallySet) return;
    const DEFAULTS = {
      subappaltatore_edile: 'reverse_charge', elettricista: 'reverse_charge', idraulico: 'reverse_charge',
      serramentista: 'vat_22', fornitore_materiali: 'vat_22', fornitore_arredi: 'vat_22',
      studio_tecnico_esterno: 'vat_22', professionista: 'vat_22', altro: 'to_verify',
      edile: 'reverse_charge', elettrico: 'reverse_charge',
    };
    const supplierVat = normalizeVat(selectedSupplier.vat_regime);
    const suggested = (supplierVat && supplierVat !== 'to_verify')
      ? supplierVat
      : (DEFAULTS[selectedSupplier.category] || 'to_verify');
    setFormData(f => ({ ...f, vat_regime: suggested }));
  }, [selectedSupplierId, selectedSupplier?.vat_regime, selectedSupplier?.category, vatRegimeManuallySet]);

  const toggleItem = (item) => {
    setSelectedItems(prev => {
      const next = { ...prev };
      if (next[item.estimate_item_id]) {
        delete next[item.estimate_item_id];
      } else {
        next[item.estimate_item_id] = {
          estimate_item_id: item.estimate_item_id,
          original_description: item.description,
          quantity: item.quantity || 1,
          unit_price: (item.unit_price || 0) * 0.6, // suggerimento -40%
        };
      }
      return next;
    });
  };

  const updateItemField = (item_id, field, value) => {
    setSelectedItems(prev => ({
      ...prev,
      [item_id]: { ...prev[item_id], [field]: parseFloat(value) || 0 },
    }));
  };

  // Applica ribasso % a tutte le voci (base = original_unit_price)
  const applyDiscountToAllItems = (percent) => {
    const factor = 1 - (percent / 100);
    let newClientTotal = 0;
    setSelectedItems(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(eid => {
        const orig = availableItems.items.find(a => a.estimate_item_id === eid);
        const basePrice = Number(orig?.unit_price || 0);
        next[eid] = { ...next[eid], unit_price: Math.round(basePrice * factor * 100) / 100 };
        newClientTotal += basePrice * (next[eid].quantity || 0);
      });
      return next;
    });
    setDiscountApplied(true);
    setDiscountConfirmed(percent > DISCOUNT_THRESHOLD);
    setShowDiscountOverModal(false);
    // Sincronizza importo concordato iniziale a partire dal ribasso applicato
    const amt = Math.round(newClientTotal * (1 - percent / 100) * 100) / 100;
    setAgreedAmount(String(amt));
    setAgreedInitialLocked(amt);
    toast.success(`Ribasso ${percent}% applicato a ${Object.keys(selectedItems).length} voci`);
  };

  // HOTFIX: Distribuzione importo concordato sulle righe (Decimal-safe, largest remainder)
  const distributeAgreedOnItems = (agreed) => {
    const cents = Math.round(agreed * 100);
    // Peso per riga in centesimi (valore cliente)
    const eids = Object.keys(selectedItems);
    const weights = eids.map(eid => {
      const orig = availableItems.items.find(a => a.estimate_item_id === eid);
      const oup = Number(orig?.unit_price || 0);
      const q = Number(selectedItems[eid].quantity || 0);
      return Math.round(oup * q * 100);
    });
    const totalW = weights.reduce((s, w) => s + w, 0);
    const out = {};
    if (totalW <= 0) {
      eids.forEach(eid => { out[eid] = { ...selectedItems[eid], unit_price: 0 }; });
      return out;
    }
    // floor allocation + remainders
    const raw = weights.map(w => (cents * w) / totalW);
    let alloc = raw.map(r => Math.floor(r));
    const remainders = raw.map((r, i) => r - alloc[i]);
    let diff = cents - alloc.reduce((s, a) => s + a, 0);
    const order = remainders.map((r, i) => [r, i]).sort((a, b) => diff > 0 ? b[0] - a[0] : a[0] - b[0]).map(p => p[1]);
    const step = diff > 0 ? 1 : -1;
    let k = 0;
    while (diff !== 0 && k < order.length * 10) {
      alloc[order[k % order.length]] += step;
      diff -= step;
      k++;
    }
    // Prezzi unitari dai total interi
    const unitPrices = alloc.map((totalCents, i) => {
      const q = Number(selectedItems[eids[i]].quantity || 0);
      if (q <= 0) return 0;
      // 4 dp
      return Math.round((totalCents / 100) / q * 10000) / 10000;
    });
    // Ricalcolo total a 2 dp e correzione delta sulla riga più pesante
    const computed = unitPrices.map((up, i) => {
      const q = Number(selectedItems[eids[i]].quantity || 0);
      return Math.round(up * q * 100);
    });
    const delta = cents - computed.reduce((s, c) => s + c, 0);
    if (delta !== 0) {
      let heaviest = 0;
      for (let i = 1; i < weights.length; i++) if (weights[i] > weights[heaviest]) heaviest = i;
      computed[heaviest] += delta;
      const q = Number(selectedItems[eids[heaviest]].quantity || 0);
      if (q > 0) unitPrices[heaviest] = Math.round((computed[heaviest] / 100) / q * 10000) / 10000;
    }
    eids.forEach((eid, i) => {
      out[eid] = { ...selectedItems[eid], unit_price: unitPrices[i] };
    });
    return out;
  };

  const applyAgreedAmountToAllItems = (amount) => {
    const distributed = distributeAgreedOnItems(amount);
    setSelectedItems(distributed);
    setDiscountApplied(true);
    setAgreedInitialLocked(amount);
    // Sincronizza il ribasso derivato
    if (totals.sold > 0) {
      const dp = Math.round((1 - amount / totals.sold) * 10000) / 100;
      setDiscountPercent(String(dp));
      setDiscountConfirmed(dp > DISCOUNT_THRESHOLD || negativeMarginConfirmed);
    }
    setShowNegativeMarginModal(false);
    setShowDiscountOverModal(false);
    toast.success(`Importo concordato ${formatCurrency(amount)} distribuito su ${Object.keys(selectedItems).length} voci`);
  };

  // Handler unico "Applica alle lavorazioni" — sceglie in base al campo compilato
  const applyPricingToItems = () => {
    const amt = parseFloat(agreedAmount);
    const dp = parseFloat(discountPercent);
    const hasAmount = !isNaN(amt) && amt >= 0;
    const hasDiscount = !isNaN(dp) && dp >= 0 && dp < 100;
    // Priorità: se importo modificato e non coincide col derivato -> usa importo
    if (hasAmount) {
      const cv = totals.sold;
      const derived = cv > 0 ? Math.round((1 - amt / cv) * 10000) / 100 : null;
      // Check margine negativo
      if (amt > cv && cv > 0 && !negativeMarginConfirmed) {
        setShowNegativeMarginModal(true);
        return;
      }
      // Check soglia 30% derivata
      if (derived !== null && derived > DISCOUNT_THRESHOLD && !discountConfirmed) {
        setShowDiscountOverModal(true);
        return;
      }
      applyAgreedAmountToAllItems(amt);
      return;
    }
    if (hasDiscount) {
      if (dp > DISCOUNT_THRESHOLD && !discountConfirmed) { setShowDiscountOverModal(true); return; }
      applyDiscountToAllItems(dp);
      return;
    }
    toast.error('Compila Ribasso % oppure Importo concordato');
  };

  const totals = React.useMemo(() => {
    let sold = 0, cost = 0;
    Object.values(selectedItems).forEach(si => {
      const orig = availableItems.items.find(a => a.estimate_item_id === si.estimate_item_id);
      const q = si.quantity || 0;
      sold += (orig?.unit_price || 0) * q;
      cost += (si.unit_price || 0) * q;
    });
    return {
      sold: Math.round(sold * 100) / 100,
      cost: Math.round(cost * 100) / 100,
      margin: Math.round((sold - cost) * 100) / 100,
      margin_pct: sold > 0 ? Math.round(((sold - cost) / sold) * 10000) / 100 : 0,
    };
  }, [selectedItems, availableItems]);

  const handleSubmit = async () => {
    // Check ribasso modificato ma non applicato
    const dp = parseFloat(discountPercent);
    if (isDiscountEligible && !isNaN(dp) && dp > 0 && !discountApplied) {
      if (!window.confirm('La percentuale indicata non coincide con i prezzi attualmente applicati alle lavorazioni.\n\nVuoi salvare mantenendo i prezzi attuali (OK) oppure annullare per applicare il ribasso (Annulla)?')) {
        return; // annulla salvataggio
      }
    }
    setSaving(true);
    try {
      const discountNum = parseFloat(discountPercent);
      const payload = {
        project_id: projectId,
        estimate_id: availableItems.estimate_id,
        supplier_id: selectedSupplierId,
        contract_type: 'a_misura',
        ...formData,
        items: Object.values(selectedItems),
        // Ribasso Step 1: solo se eligible + valore valido, altrimenti pulisci
        ...(isDiscountEligible && !isNaN(discountNum) && discountNum > 0 ? {
          contract_discount_percent: discountNum,
          contract_discount_confirmed_over_threshold: discountConfirmed,
        } : (isEdit ? {
          contract_discount_percent: null,
          contract_discount_confirmed_over_threshold: false,
        } : {})),
        // HOTFIX: Importo concordato iniziale (solo se eligible + lock valido)
        ...(isDiscountEligible && agreedInitialLocked != null && agreedInitialLocked > 0 ? {
          agreed_initial_amount: agreedInitialLocked,
          confirm_negative_margin: negativeMarginConfirmed,
        } : (isEdit ? {
          agreed_initial_amount: null,
          confirm_negative_margin: false,
        } : {})),
      };
      if (isEdit) {
        await axios.put(`${API}/api/subcontracts/${existingSubcontract.id}`, payload);
        toast.success('Bozza aggiornata');
      } else {
        await axios.post(`${API}/api/subcontracts`, payload);
        toast.success('Subappalto creato in bozza');
      }
      onCreated?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === 'object' && detail.error === 'voci_gia_affidate') {
        toast.error(`${detail.message}. Ricarica e riprova.`);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Errore creazione');
      }
    } finally {
      setSaving(false);
    }
  };

  const canNext = () => {
    if (step === 1) return !!selectedSupplierId;
    if (step === 2) return Object.keys(selectedItems).length > 0;
    if (step === 3) return Object.values(selectedItems).every(i => i.quantity > 0 && i.unit_price >= 0);
    return true;
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="subcontract-wizard">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Modifica ${existingSubcontract?.subcontract_number || 'bozza'} — Step ${step} di 4` : `Nuovo Subappalto — Step ${step} di 4`}</DialogTitle>
          <DialogDescription className="sr-only">
            Wizard di {isEdit ? 'modifica' : 'creazione'} subappalto in 4 step: fornitore, voci, dettagli contrattuali, revisione.
          </DialogDescription>
          <div className="flex gap-1 mt-2">
            {[1, 2, 3, 4].map(s => (
              <div key={s} className={`h-2 flex-1 rounded ${s <= step ? 'bg-[#FF7819]' : 'bg-slate-200'}`} />
            ))}
          </div>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4" data-testid="wizard-step-1">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-sm uppercase tracking-wide text-[#525252]">Subappaltatore</h4>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setQuickCreateOpen(true)}
                data-testid="wizard-quick-create-supplier"
                className="border-[#FF7819] text-[#FF7819] hover:bg-orange-50"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                Nuovo subappaltatore
              </Button>
            </div>
            <p className="text-xs text-[#737373]">Seleziona dall&apos;anagrafica esistente oppure crea un nuovo subappaltatore.</p>
            <Select value={selectedSupplierId} onValueChange={setSelectedSupplierId}>
              <SelectTrigger data-testid="supplier-select">
                <SelectValue placeholder="Scegli subappaltatore..." />
              </SelectTrigger>
              <SelectContent>
                {suppliers.map(s => {
                  const catLabel = ({
                    subappaltatore_edile: 'Subappaltatore edile',
                    elettricista: 'Elettricista',
                    idraulico: 'Idraulico',
                    serramentista: 'Serramentista',
                    fornitore_materiali: 'Fornitore materiali',
                    fornitore_arredi: 'Fornitore arredi',
                    studio_tecnico_esterno: 'Studio tecnico esterno',
                    professionista: 'Professionista',
                    altro: 'Altro',
                    // legacy
                    edile: 'Subappaltatore edile',
                    elettrico: 'Elettricista',
                    serramenti: 'Serramentista',
                    finiture: 'Altro',
                    impianti: 'Altro',
                  })[s.category] || s.category;
                  return (
                    <SelectItem key={s.id} value={s.id}>
                      {s.ragione_sociale} — {catLabel} — P.IVA {s.partita_iva}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {selectedSupplierId && (
              <div className="border rounded p-3 bg-slate-50 text-sm">
                {(() => {
                  const s = suppliers.find(x => x.id === selectedSupplierId);
                  if (!s) return null;
                  return (
                    <div>
                      <div className="font-semibold">{s.ragione_sociale}</div>
                      <div className="text-xs text-[#737373]">
                        {s.sede_legale || '—'} · P.IVA {s.partita_iva} · CF {s.codice_fiscale}
                        {s.legale_rappresentante && <> · LR: {s.legale_rappresentante}</>}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
            <p className="text-xs text-[#a3a3a3]">
              Per la gestione completa dei documenti (DURC, POS, polizze) vai a <a href="/admin/suppliers" target="_blank" rel="noreferrer" className="text-[#FF7819] underline">Anagrafica Subappaltatori</a>.
            </p>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="wizard-step-2">
            <h4 className="font-semibold text-sm uppercase tracking-wide text-[#525252]">Voci da affidare</h4>
            <p className="text-xs text-[#737373]">Voci dal preventivo. C/D/S consigliate; F/G/H opzionali; A/B non ammesse.</p>
            <div className="border rounded max-h-[400px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white shadow-sm">
                  <tr className="text-left">
                    <th className="px-2 py-2 w-10"></th>
                    <th className="px-2 py-2">Cat.</th>
                    <th className="px-2 py-2">Codice</th>
                    <th className="px-2 py-2">Descrizione</th>
                    <th className="px-2 py-2 text-right">Qtà</th>
                    <th className="px-2 py-2 text-right">Prezzo</th>
                    <th className="px-2 py-2">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {availableItems.items.map(it => {
                    // In edit mode, un item gia' assegnato a QUESTO subappalto va trattato come libero
                    const assignedToSelf = isEdit && it.assigned_to_subcontract_number === existingSubcontract?.subcontract_number;
                    const effectivelyAssigned = it.is_assigned && !assignedToSelf;
                    const disabled = effectivelyAssigned || !it.is_allowed_category;
                    const selected = !!selectedItems[it.estimate_item_id];
                    return (
                      <tr key={it.estimate_item_id} className={`border-t ${disabled ? 'opacity-50 bg-slate-50' : ''} ${selected ? 'bg-orange-50' : ''}`}>
                        <td className="px-2 py-1.5">
                          <input
                            type="checkbox"
                            checked={selected}
                            disabled={disabled}
                            onChange={() => toggleItem(it)}
                            data-testid={`item-checkbox-${it.estimate_item_id}`}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Badge variant="outline" className={it.is_default_category ? '' : 'bg-slate-100'}>
                            {it.category || '—'}
                          </Badge>
                        </td>
                        <td className="px-2 py-1.5 font-mono text-[10px]">{it.code || '—'}</td>
                        <td className="px-2 py-1.5 max-w-xs truncate" title={it.description}>{it.description}</td>
                        <td className="px-2 py-1.5 text-right">{it.quantity}</td>
                        <td className="px-2 py-1.5 text-right">{formatCurrency(it.unit_price)}</td>
                        <td className="px-2 py-1.5 text-xs">
                          {effectivelyAssigned ? (
                            <Badge variant="outline" className="bg-rose-50 text-rose-700 text-[10px]">
                              → {it.assigned_to_subcontract_number}
                            </Badge>
                          ) : !it.is_allowed_category ? (
                            <span className="text-[10px] text-rose-600">Non ammessa</span>
                          ) : assignedToSelf ? (
                            <span className="text-[10px] text-orange-600">In modifica</span>
                          ) : (
                            <span className="text-[10px] text-emerald-600">Libera</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-[#737373]">Selezionate: <strong>{Object.keys(selectedItems).length}</strong></p>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3" data-testid="wizard-step-3">
            <h4 className="font-semibold text-sm uppercase tracking-wide text-[#525252]">Prezzi e condizioni</h4>

            {/* Riepilogo economico Step 3 (dinamico per categoria) */}
            {(() => {
              const effective = totals.sold > 0 ? Math.round((1 - totals.cost / totals.sold) * 10000) / 100 : null;
              const initialNum = parseFloat(discountPercent);
              const hasInitial = isDiscountEligible && !isNaN(initialNum) && initialNum > 0;
              const hasInitialAmt = isDiscountEligible && agreedInitialLocked != null && agreedInitialLocked > 0;
              return (
                <div className={`grid gap-2 ${isDiscountEligible ? 'grid-cols-2 md:grid-cols-4 xl:grid-cols-7' : 'grid-cols-2 md:grid-cols-4'}`} data-testid="wizard-step3-kpi">
                  <KpiMini label="Valore lavori cliente" value={formatCurrency(totals.sold)} testId="kpi-sold" />
                  {isDiscountEligible && (
                    <KpiMini
                      label={'Importo concordato\niniziale'}
                      value={hasInitialAmt ? formatCurrency(agreedInitialLocked) : '—'}
                      testId="kpi-agreed-initial"
                    />
                  )}
                  <KpiMini
                    label={isDiscountEligible ? 'Valore contratto\neffettivo' : 'Valore contratto affidato'}
                    value={formatCurrency(totals.cost)}
                    testId="kpi-cost"
                  />
                  <KpiMini label="Margine effettivo" value={formatCurrency(totals.margin)} accent={totals.margin >= 0 ? 'emerald' : 'rose'} testId="kpi-margin" />
                  <KpiMini label="Margine %" value={`${(totals.margin_pct || 0).toFixed(2)}%`} accent={parseFloat(totals.margin_pct) >= 0 ? 'emerald' : 'rose'} testId="kpi-margin-pct" />
                  {isDiscountEligible && (
                    <>
                      <KpiMini label="Ribasso iniziale" value={hasInitial ? `${initialNum.toFixed(2)}%` : '—'} testId="kpi-initial-discount" />
                      <KpiMini label="Ribasso effettivo" value={effective != null ? `${effective.toFixed(2)}%` : '—'} testId="kpi-effective-discount" />
                    </>
                  )}
                </div>
              );
            })()}

            {/* Prezzo consigliato di subappalto impianto elettrico (solo informativo) */}
            {/* Visibile quando il PERIMETRO contiene voci categoria D (nuova tassonomia),
                indipendentemente dall'anagrafica del subappaltatore. */}
            {recommendedHasElectrical && (
              <div className="p-3 bg-[#FFF4EC] border border-[#FFD9BD] rounded space-y-1" data-testid="recommended-subcontract-panel">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-[#8A4B16]">Prezzo consigliato di subappalto</span>
                  <span className="text-lg font-bold text-[#FF7819]" data-testid="recommended-subcontract-price">
                    {recommendedPrice != null ? formatCurrency(recommendedPrice) : '—'}
                  </span>
                </div>
                <p className="text-xs text-[#9A6A40]">
                  Calcolato sulle quantità delle lavorazioni elettriche previste e sul prezzario impiantista di riferimento. Valore solo informativo: l'importo contrattuale resta liberamente modificabile.
                </p>
                {recommendedPriceIncomplete && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1" data-testid="recommended-incomplete-warning">
                    Prezzo consigliato INCOMPLETO — costo impiantista mancante per {recommendedMissing.length} lavorazione/i: {recommendedMissing.map(m => m.code).join(', ')}
                  </p>
                )}
              </div>
            )}

            {/* Campo ribasso / importo concordato — SOLO categorie eligible */}
            {isDiscountEligible && (
              <div className="p-3 bg-slate-50 border border-slate-200 rounded space-y-2" data-testid="discount-panel">
                <div className="flex items-end gap-3 flex-wrap">
                  <div>
                    <label className="text-xs font-semibold text-[#525252] block mb-1">Ribasso iniziale %</label>
                    <Input
                      type="number" step="0.5" min="0" max="99.99"
                      value={discountPercent}
                      onChange={e => {
                        const v = e.target.value;
                        setDiscountPercent(v);
                        setDiscountApplied(false);
                        // Sync live: aggiorna Importo se cliente > 0
                        const p = parseFloat(v);
                        if (!isNaN(p) && totals.sold > 0) {
                          const amt = Math.round(totals.sold * (1 - p / 100) * 100) / 100;
                          setAgreedAmount(String(amt));
                        }
                      }}
                      placeholder="es. 18"
                      className="w-32"
                      data-testid="discount-percent-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#525252] block mb-1">Importo concordato iniziale €</label>
                    <Input
                      type="number" step="0.01" min="0"
                      value={agreedAmount}
                      onChange={e => {
                        const v = e.target.value;
                        setAgreedAmount(v);
                        setAgreedTouched(true);
                        setDiscountApplied(false);
                        // Sync live: aggiorna Ribasso derivato
                        const a = parseFloat(v);
                        if (!isNaN(a) && totals.sold > 0) {
                          const dp = Math.round((1 - a / totals.sold) * 10000) / 100;
                          setDiscountPercent(String(dp));
                        }
                      }}
                      placeholder={totals.sold > 0 ? String(totals.sold.toFixed(2)) : 'es. 55000'}
                      className="w-40"
                      data-testid="agreed-amount-input"
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={applyPricingToItems}
                    disabled={!discountPercent && !agreedAmount}
                    className="bg-[#FF7819] hover:bg-[#e56811] text-white"
                    data-testid="apply-pricing-btn"
                  >
                    Applica alle lavorazioni
                  </Button>
                  {(discountPercent || agreedAmount) && !discountApplied && (
                    <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="discount-modified-not-applied">
                      Valore modificato ma non ancora applicato alle lavorazioni
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500">
                  Contratto <strong>a misura</strong>: l&apos;importo iniziale determina i prezzi unitari
                  di partenza, ma la contabilizzazione finale resta sulle quantità effettivamente
                  eseguite e approvate.
                </div>
              </div>
            )}

            <div className="border rounded max-h-[300px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white">
                  <tr>
                    <th className="px-2 py-2 text-left">Voce</th>
                    <th className="px-2 py-2 text-right">Q.tà affidata</th>
                    <th className="px-2 py-2 text-right">Prezzo unit. sub</th>
                    <th className="px-2 py-2 text-right">Prezzo cliente</th>
                    <th className="px-2 py-2 text-right">Margine</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.values(selectedItems).map(si => {
                    const orig = availableItems.items.find(a => a.estimate_item_id === si.estimate_item_id);
                    const margin = ((orig?.unit_price || 0) - si.unit_price) * si.quantity;
                    return (
                      <tr key={si.estimate_item_id} className="border-t">
                        <td className="px-2 py-1.5 max-w-[240px] truncate" title={si.original_description}>{si.original_description}</td>
                        <td className="px-2 py-1.5 text-right">
                          <Input type="number" step="0.01" value={si.quantity}
                            onChange={e => updateItemField(si.estimate_item_id, 'quantity', e.target.value)}
                            className="h-7 text-xs w-24 text-right" data-testid={`qty-${si.estimate_item_id}`} />
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <Input type="number" step="0.01" value={si.unit_price}
                            onChange={e => updateItemField(si.estimate_item_id, 'unit_price', e.target.value)}
                            className="h-7 text-xs w-28 text-right" data-testid={`price-${si.estimate_item_id}`} />
                        </td>
                        <td className="px-2 py-1.5 text-right text-[#737373]">{formatCurrency(orig?.unit_price || 0)}</td>
                        <td className={`px-2 py-1.5 text-right font-semibold ${margin >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {formatCurrency(margin)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-orange-50 font-semibold border-t-2 border-[#FF7819]">
                  <tr>
                    <td className="px-2 py-2">Totali</td>
                    <td className="px-2 py-2 text-right">—</td>
                    <td className="px-2 py-2 text-right">Affidato: {formatCurrency(totals.cost)}</td>
                    <td className="px-2 py-2 text-right">Venduto: {formatCurrency(totals.sold)}</td>
                    <td className={`px-2 py-2 text-right ${totals.margin >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {formatCurrency(totals.margin)} ({totals.margin_pct}%)
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-3 border-t">
              <div>
                <label className="text-xs text-[#525252] uppercase font-semibold">Regime fiscale / IVA</label>
                <Select
                  value={formData.vat_regime}
                  onValueChange={v => {
                    setVatRegimeManuallySet(true);
                    setFormData(f => ({ ...f, vat_regime: v }));
                  }}
                >
                  <SelectTrigger data-testid="vat-regime-select" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VAT_REGIMES.filter(v => !['iva_10','iva_22','fuori_campo','da_verificare'].includes(v.value)).map(v => (
                      <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedSupplier && (
                  <div className="text-[10px] text-[#a3a3a3] mt-0.5">
                    Anagrafica fornitore: {VAT_REGIMES.find(o => o.value === normalizeVat(selectedSupplier.vat_regime))?.label || 'Da verificare'}
                    {' · '}Modifica valida solo per questo contratto.
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs text-[#525252] uppercase font-semibold">Firmatario appaltatrice</label>
                <Select value={formData.contractor_firmatario} onValueChange={v => setFormData(f => ({ ...f, contractor_firmatario: v }))}>
                  <SelectTrigger data-testid="firmatario-select" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gaetano_falco">Gaetano Falco</SelectItem>
                    <SelectItem value="antonio_loparco">Antonio Loparco</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#525252] uppercase font-semibold">Data inizio</label>
                <Input type="date" value={formData.start_date} onChange={e => setFormData(f => ({ ...f, start_date: e.target.value }))} className="h-9" />
              </div>
              <div>
                <label className="text-xs text-[#525252] uppercase font-semibold">Data fine</label>
                <Input type="date" value={formData.end_date} onChange={e => setFormData(f => ({ ...f, end_date: e.target.value }))} className="h-9" />
              </div>
              <div>
                <label className="text-xs text-[#525252] uppercase font-semibold">Penali giorno</label>
                <Input type="number" value={formData.penali_giornaliere} onChange={e => setFormData(f => ({ ...f, penali_giornaliere: parseFloat(e.target.value) || 0 }))} className="h-9" />
              </div>
              <div>
                <label className="text-xs text-[#525252] uppercase font-semibold">Ritenuta garanzia %</label>
                <Input type="number" step="0.1" value={formData.ritenuta_garanzia_percent} onChange={e => setFormData(f => ({ ...f, ritenuta_garanzia_percent: parseFloat(e.target.value) || 0 }))} className="h-9" data-testid="ritenuta-input" />
              </div>
              <div>
                <label className="text-xs text-[#525252] uppercase font-semibold">Polizza RCT/RCO massimale</label>
                <Input type="number" value={formData.polizza_massimale} onChange={e => setFormData(f => ({ ...f, polizza_massimale: parseFloat(e.target.value) || 0 }))} className="h-9" />
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3" data-testid="wizard-step-4">
            <h4 className="font-semibold text-sm uppercase tracking-wide text-[#525252]">Riepilogo</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="border rounded p-3 bg-slate-50">
                <div className="text-xs text-[#737373] uppercase">Subappaltatore</div>
                <div className="font-semibold">{suppliers.find(s => s.id === selectedSupplierId)?.ragione_sociale || '—'}</div>
              </div>
              <div className="border rounded p-3 bg-slate-50">
                <div className="text-xs text-[#737373] uppercase">Firmatario appaltatrice</div>
                <div className="font-semibold">
                  {formData.contractor_firmatario === 'gaetano_falco' ? 'Gaetano Falco' : 'Antonio Loparco'}
                </div>
              </div>
              <div className="border rounded p-3 bg-slate-50">
                <div className="text-xs text-[#737373] uppercase">Voci affidate</div>
                <div className="font-semibold">{Object.keys(selectedItems).length}</div>
              </div>
              <div className="border rounded p-3 bg-slate-50">
                <div className="text-xs text-[#737373] uppercase">Regime fiscale</div>
                <div className="font-semibold">{VAT_REGIMES.find(v => v.value === formData.vat_regime)?.label}</div>
              </div>
            </div>
            <div className="border-2 border-[#FF7819] bg-orange-50 rounded p-4 mt-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-xs text-[#737373] uppercase">Venduto al cliente</div>
                  <div className="text-xl font-bold text-[#3C3C3C]">{formatCurrency(totals.sold)}</div>
                </div>
                <div>
                  <div className="text-xs text-[#737373] uppercase">Affidato subappaltatore</div>
                  <div className="text-xl font-bold text-[#3C3C3C]">{formatCurrency(totals.cost)}</div>
                </div>
                <div>
                  <div className="text-xs text-[#737373] uppercase">Margine previsto</div>
                  <div className={`text-xl font-bold ${totals.margin >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {formatCurrency(totals.margin)} <span className="text-sm">({totals.margin_pct}%)</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="text-xs text-[#737373] bg-slate-50 p-3 rounded">
              <strong>Cosa succederà:</strong> il contratto sarà creato in stato <strong>Bozza</strong>. Le voci selezionate saranno bloccate a livello DB (impossibile assegnarle ad altri subappalti). Potrai generare il PDF e caricarlo firmato dalla tabella. Il passaggio a &quot;Firmato&quot; (con sync Q.E.) sarà disponibile in Fase C.
            </div>
          </div>
        )}

        <DialogFooter className="flex justify-between">
          <div className="flex gap-2">
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="wizard-prev">
                <ChevronLeft className="w-4 h-4 mr-1" /> Indietro
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Annulla</Button>
            {step < 4 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canNext()}
                className="bg-[#FF7819] hover:bg-[#e56811] text-white"
                data-testid="wizard-next"
              >
                Avanti <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="wizard-submit"
              >
                <Check className="w-4 h-4 mr-1" />
                {saving ? 'Salvataggio...' : 'Crea Bozza'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
      {quickCreateOpen && (
        <QuickCreateSupplierModal
          onClose={() => setQuickCreateOpen(false)}
          onCreated={(sup) => {
            setSuppliers(prev => [sup, ...prev]);
            setSelectedSupplierId(sup.id);
            setQuickCreateOpen(false);
          }}
        />
      )}

      {/* AlertDialog — Warning ribasso oltre soglia 30% */}
      <AlertDialog open={showDiscountOverModal} onOpenChange={(o) => { if (!o) setShowDiscountOverModal(false); }}>
        <AlertDialogContent data-testid="discount-over-threshold-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
              Ribasso oltre soglia consigliata
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#3C3C3C] pt-2">
              Il ribasso <strong>{discountPercent}%</strong> supera la soglia consigliata del {DISCOUNT_THRESHOLD}%.
              Verificare <strong>attentamente</strong> la sostenibilità economica dell&apos;affidamento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded p-3 my-2">
            <input
              type="checkbox"
              id="confirm-discount-cb"
              checked={discountConfirmed}
              onChange={(e) => setDiscountConfirmed(e.target.checked)}
              data-testid="discount-confirm-checkbox"
              className="mt-0.5"
            />
            <label htmlFor="confirm-discount-cb" className="text-xs text-amber-900">
              Confermo di aver verificato la sostenibilità economica del ribasso applicato.
            </label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setDiscountConfirmed(false); }} data-testid="discount-cancel-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction
              disabled={!discountConfirmed}
              onClick={() => {
                // Se il ribasso deriva da un importo concordato, applica l'importo
                const amt = parseFloat(agreedAmount);
                if (!isNaN(amt) && amt >= 0) {
                  applyAgreedAmountToAllItems(amt);
                } else {
                  applyDiscountToAllItems(parseFloat(discountPercent));
                }
              }}
              className="bg-amber-600 hover:bg-amber-700 disabled:opacity-40"
              data-testid="discount-proceed-btn"
            >
              Procedi con ribasso {discountPercent}%
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AlertDialog — Warning margine negativo (agreed > client value) */}
      <AlertDialog open={showNegativeMarginModal} onOpenChange={(o) => { if (!o) setShowNegativeMarginModal(false); }}>
        <AlertDialogContent data-testid="negative-margin-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-800">
              <AlertTriangle className="w-6 h-6 text-rose-500" />
              Margine negativo — importo superiore al valore cliente
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#3C3C3C] pt-2">
              L&apos;importo concordato <strong>{formatCurrency(parseFloat(agreedAmount) || 0)}</strong> supera il
              valore delle lavorazioni vendute al cliente (<strong>{formatCurrency(totals.sold)}</strong>) e
              determina un <strong>margine negativo</strong>. Verificare prima di procedere.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded p-3 my-2">
            <input
              type="checkbox"
              id="confirm-neg-margin-cb"
              checked={negativeMarginConfirmed}
              onChange={(e) => setNegativeMarginConfirmed(e.target.checked)}
              data-testid="negative-margin-confirm-checkbox"
              className="mt-0.5"
            />
            <label htmlFor="confirm-neg-margin-cb" className="text-xs text-rose-900">
              Confermo di aver verificato che l&apos;importo concordato determina un margine negativo.
            </label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setNegativeMarginConfirmed(false); }} data-testid="negative-margin-cancel-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction
              disabled={!negativeMarginConfirmed}
              onClick={() => applyAgreedAmountToAllItems(parseFloat(agreedAmount))}
              className="bg-rose-600 hover:bg-rose-700 disabled:opacity-40"
              data-testid="negative-margin-proceed-btn"
            >
              Procedi con margine negativo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

// =========================================================
// KPI Mini Card — Step 3 wizard riepilogo economico
// =========================================================
// =========================================================
// STEP 2 (2026-02): Widget "Ultima versione contratto firmato"
// =========================================================
const SignedDocumentWidget = ({ subcontract, onDownload, onUpload, onDownloadHistory, onRegenerate }) => {
  const [showHistory, setShowHistory] = useState(false);
  const docs = subcontract.documents || [];
  const signedActive = docs.find(
    d => (d.document_type === 'firmato' || d.document_type === 'contratto_firmato') && d.is_active !== false
  );
  const history = docs
    .filter(d =>
      (d.document_type === 'firmato' || d.document_type === 'contratto_firmato' || d.document_type === 'sostituito')
      && d.id !== signedActive?.id
    )
    .sort((a, b) => (b.version || 0) - (a.version || 0));

  const _formatSize = (b) => {
    if (!b) return '—';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(2)} MB`;
  };
  const _formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  return (
    <div className="bg-slate-50 border-l-4 border-slate-300 p-3 rounded-r" data-testid={`signed-widget-${subcontract.id}`}>
      <div className="text-[11px] uppercase tracking-wide text-slate-600 font-semibold mb-2">
        Ultima versione contratto firmato
      </div>
      {signedActive ? (
        <div className="space-y-2">
          <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-xs">
            <div>
              <span className="text-slate-500">Nome file:</span>{' '}
              <span className="font-medium text-slate-900" data-testid={`signed-filename-${subcontract.id}`}>
                {signedActive.original_filename || signedActive.filename}
              </span>
            </div>
            <div>
              <span className="text-slate-500">Versione:</span>{' '}
              <Badge variant="outline" className="bg-emerald-50 text-emerald-800 text-[10px]" data-testid={`signed-version-${subcontract.id}`}>
                v{signedActive.version || 1} · Attiva
              </Badge>
            </div>
            <div>
              <span className="text-slate-500">Caricato:</span>{' '}
              <span className="text-slate-800" data-testid={`signed-date-${subcontract.id}`}>
                {_formatDate(signedActive.uploaded_at)}
              </span>
            </div>
            <div>
              <span className="text-slate-500">Dimensione:</span>{' '}
              <span className="text-slate-800">{_formatSize(signedActive.file_size)}</span>
            </div>
            {signedActive.sha256 && (
              <div title={`SHA256 completo: ${signedActive.sha256}`}>
                <span className="text-slate-500">Impronta SHA256 (integrità):</span>{' '}
                <code className="text-[10px] bg-slate-200 px-1 py-0.5 rounded" data-testid={`signed-sha256-${subcontract.id}`}>
                  {signedActive.sha256.substring(0, 12)}…
                </code>
              </div>
            )}
            {signedActive.storage_provider === 'r2' && (
              <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700">R2</Badge>
            )}
          </div>
          <div className="flex items-center flex-wrap gap-2">
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs"
              onClick={() => onDownload(subcontract, 'firmato')}
              data-testid={`download-signed-${subcontract.id}`}
            >
              Scarica contratto firmato
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={() => onRegenerate(subcontract)}
              data-testid={`regen-draft-${subcontract.id}`}
            >
              Rigenera bozza PDF
            </Button>
            <label>
              <input
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={e => e.target.files?.[0] && onUpload(subcontract, e.target.files[0])}
                data-testid={`upload-new-version-${subcontract.id}`}
              />
              <Button size="sm" variant="outline" asChild className="h-8 text-xs">
                <span>Carica nuova versione firmata</span>
              </Button>
            </label>
          </div>
          {history.length > 0 && (
            <div className="pt-2 border-t border-slate-200">
              <button
                type="button"
                className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1"
                onClick={() => setShowHistory(v => !v)}
                data-testid={`toggle-history-${subcontract.id}`}
              >
                <span>{showHistory ? '▾' : '▸'}</span>
                Versioni firmate precedenti ({history.length})
              </button>
              {showHistory && (
                <div className="mt-2 space-y-1" data-testid={`history-list-${subcontract.id}`}>
                  {history.map(h => (
                    <div key={h.id} className="flex items-center flex-wrap gap-x-3 gap-y-1 text-[11px] bg-white border border-slate-200 rounded px-2 py-1.5">
                      <Badge variant="outline" className="bg-slate-100 text-slate-700 text-[10px]">
                        v{h.version || 1} · Sostituito
                      </Badge>
                      <span className="text-slate-900 font-medium">{h.original_filename || h.filename}</span>
                      <span className="text-slate-500">{_formatDate(h.uploaded_at)}</span>
                      {h.sha256 && (
                        <code className="text-[10px] bg-slate-100 px-1 py-0.5 rounded" title={h.sha256}>
                          {h.sha256.substring(0, 12)}…
                        </code>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 text-[10px] ml-auto"
                        onClick={() => onDownloadHistory(subcontract, h.id, h.filename || h.original_filename)}
                        data-testid={`download-history-${h.id}`}
                      >
                        Scarica
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="text-sm text-slate-600" data-testid={`no-signed-${subcontract.id}`}>
            Contratto firmato non ancora caricato
          </div>
          <label>
            <input
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={e => e.target.files?.[0] && onUpload(subcontract, e.target.files[0])}
              data-testid={`upload-first-signed-${subcontract.id}`}
            />
            <Button size="sm" asChild className="bg-[#FF7819] hover:bg-[#e56811] text-white h-8 text-xs">
              <span>Carica contratto firmato</span>
            </Button>
          </label>
        </div>
      )}
    </div>
  );
};



const KpiMini = ({ label, value, accent, testId }) => {  const color = accent === 'emerald' ? 'text-emerald-700' : accent === 'rose' ? 'text-rose-700' : 'text-[#3C3C3C]';
  return (
    <div className="bg-white border border-slate-200 rounded px-2 py-1.5" data-testid={testId}>
      <div className="text-[9px] uppercase tracking-wide text-[#737373] font-semibold truncate">{label}</div>
      <div className={`text-sm font-bold mt-0.5 ${color} truncate`}>{value}</div>
    </div>
  );
};

// =========================================================
// QUICK CREATE SUPPLIER — modal rapido dal wizard
// =========================================================
const QuickCreateSupplierModal = ({ onClose, onCreated }) => {
  // Defaults regime fiscale per categoria (2026-02 STEP 2)
  const _VAT_DEFAULT_BY_CAT = {
    subappaltatore_edile: 'reverse_charge', elettricista: 'reverse_charge', idraulico: 'reverse_charge',
    serramentista: 'vat_22', fornitore_materiali: 'vat_22', fornitore_arredi: 'vat_22',
    studio_tecnico_esterno: 'vat_22', professionista: 'vat_22', altro: 'to_verify',
  };
  const _VAT_OPTIONS = [
    { value: 'reverse_charge', label: 'Reverse charge' },
    { value: 'vat_4', label: 'IVA 4%' },
    { value: 'vat_10', label: 'IVA 10%' },
    { value: 'vat_22', label: 'IVA 22%' },
    { value: 'exempt', label: 'Esente IVA' },
    { value: 'out_of_scope', label: 'Fuori campo IVA' },
    { value: 'not_subject', label: 'Non soggetto IVA' },
    { value: 'to_verify', label: 'Da verificare' },
  ];
  const [form, setForm] = useState({
    ragione_sociale: '',
    category: 'subappaltatore_edile',
    vat_regime: 'reverse_charge',  // default subappaltatore_edile
    partita_iva: '',
    codice_fiscale: '',
    pec: '',
    email: '',
    telefono: '',
    sede_legale: '',
    legale_rappresentante: '',
    ccnl: '',
    cassa_edile: '',
    iban: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  // Cambio categoria: auto-suggerisci vat_regime (in Quick Create, sovrascrivi silenziosamente
  // il default finche' l'utente non modifica manualmente vat_regime, per fluidita' UX rapida).
  const setCategoryQC = (newCat) => {
    setForm(f => {
      const suggested = _VAT_DEFAULT_BY_CAT[newCat] || 'to_verify';
      // Se vat_regime corrente coincide con default della categoria precedente → aggiorna al nuovo default
      const oldDefault = _VAT_DEFAULT_BY_CAT[f.category] || 'to_verify';
      const nextVat = (f.vat_regime === oldDefault) ? suggested : f.vat_regime;
      return { ...f, category: newCat, vat_regime: nextVat };
    });
  };

  const handleSubmit = async () => {
    if (!form.ragione_sociale || !form.partita_iva || !form.codice_fiscale) {
      toast.error('Ragione sociale, P.IVA e C.F. sono obbligatori');
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/suppliers`, form);
      toast.success('Subappaltatore creato e selezionato');
      onCreated?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore creazione');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quick-create-supplier-modal">
        <DialogHeader>
          <DialogTitle>Nuovo Subappaltatore — Creazione rapida</DialogTitle>
          <DialogDescription className="sr-only">
            Modulo rapido per creare un nuovo fornitore/subappaltatore da associare al subappalto.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs uppercase text-[#525252] font-semibold">Ragione sociale *</label>
            <Input value={form.ragione_sociale} onChange={e => set('ragione_sociale', e.target.value)} data-testid="qc-ragione-sociale" />
          </div>
          <div>
            <label className="text-xs uppercase text-[#525252] font-semibold">Categoria *</label>
            <Select value={form.category} onValueChange={setCategoryQC}>
              <SelectTrigger data-testid="qc-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="subappaltatore_edile">Subappaltatore edile</SelectItem>
                <SelectItem value="elettricista">Elettricista</SelectItem>
                <SelectItem value="idraulico">Idraulico</SelectItem>
                <SelectItem value="serramentista">Serramentista</SelectItem>
                <SelectItem value="fornitore_materiali">Fornitore materiali</SelectItem>
                <SelectItem value="fornitore_arredi">Fornitore arredi</SelectItem>
                <SelectItem value="studio_tecnico_esterno">Studio tecnico esterno</SelectItem>
                <SelectItem value="professionista">Professionista</SelectItem>
                <SelectItem value="altro">Altro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase text-[#525252] font-semibold">Regime fiscale / IVA</label>
            <Select value={form.vat_regime} onValueChange={v => set('vat_regime', v)}>
              <SelectTrigger data-testid="qc-vat-regime"><SelectValue /></SelectTrigger>
              <SelectContent>
                {_VAT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase text-[#525252] font-semibold">P.IVA *</label>
            <Input value={form.partita_iva} onChange={e => set('partita_iva', e.target.value.toUpperCase())} data-testid="qc-piva" />
          </div>
          <div>
            <label className="text-xs uppercase text-[#525252] font-semibold">Codice Fiscale *</label>
            <Input value={form.codice_fiscale} onChange={e => set('codice_fiscale', e.target.value.toUpperCase())} data-testid="qc-cf" />
          </div>
          <div>
            <label className="text-xs uppercase text-[#525252] font-semibold">PEC</label>
            <Input value={form.pec} onChange={e => set('pec', e.target.value)} />
          </div>
          <div>
            <label className="text-xs uppercase text-[#525252] font-semibold">Email</label>
            <Input value={form.email} onChange={e => set('email', e.target.value)} />
          </div>
          <div>
            <label className="text-xs uppercase text-[#525252] font-semibold">Telefono</label>
            <Input value={form.telefono} onChange={e => set('telefono', e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className="text-xs uppercase text-[#525252] font-semibold">Sede legale</label>
            <Input value={form.sede_legale} onChange={e => set('sede_legale', e.target.value)} />
          </div>
          <div>
            <label className="text-xs uppercase text-[#525252] font-semibold">Legale rappresentante</label>
            <Input value={form.legale_rappresentante} onChange={e => set('legale_rappresentante', e.target.value)} />
          </div>
          <div>
            <label className="text-xs uppercase text-[#525252] font-semibold">IBAN</label>
            <Input value={form.iban} onChange={e => set('iban', e.target.value.toUpperCase())} />
          </div>
          <div>
            <label className="text-xs uppercase text-[#525252] font-semibold">CCNL</label>
            <Input value={form.ccnl} onChange={e => set('ccnl', e.target.value)} />
          </div>
          <div>
            <label className="text-xs uppercase text-[#525252] font-semibold">Cassa Edile</label>
            <Input value={form.cassa_edile} onChange={e => set('cassa_edile', e.target.value)} />
          </div>
        </div>
        <p className="text-xs text-[#737373] bg-slate-50 p-2 rounded mt-2">
          I documenti (DURC, POS, polizza, patente crediti) potranno essere caricati successivamente da <a href="/admin/suppliers" target="_blank" rel="noreferrer" className="text-[#FF7819] underline">Anagrafica Subappaltatori</a>.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-[#FF7819] hover:bg-[#e56811] text-white" data-testid="qc-save-btn">
            {saving ? 'Salvataggio...' : 'Crea e seleziona'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectSubcontractsTab;
